// A station's client and its automatic update, in words (F-STN-12, spec/10 §8, spec/13 §3). One reading of the
// venue's `GET …/station-clients` row, shared by the Stations list's compact Client cell and the station modal's
// Station client section, so both say the same thing:
//
//   unknown      the PC has not reported a version (an older station app, or never connected)
//   rolled_back  the new version did not come up healthy, the previous one was put back — a warning, with detail
//   failed       a download, a check or an install failed (retried next check) — a warning, with detail
//   checking / downloading / downloaded / installing / restarting   on its way to `targetVersion`
//   available    a newer version is offered but the updater has not started: "Update available"
//   image        a diskless PC (CCBoot) is offered a newer version: it does not update itself — what it installs is lost
//                when it restarts — so the image is updated instead: "Update the image" (2026-10-07, spec/10 §14)
//   current      running what it should
//
// Since 2026-10-07 (spec/10 §8.3) a station also reports its **phase** and, while it downloads, its **progress**: the
// line under the badge then says it in plain words — "Downloading update 1.10.8500 — 45%", "Installing — the PC
// restarts in a moment", "Up to date (1.10.8500)", "Update failed: …" — with a progress bar while it downloads. A PC on
// an older app reports neither, and the line falls back to the state. A PC that says how fast it downloads (2026-10-07)
// adds it: "Downloading update 1.10.8500 — 45% · 12 MB/s".
//
// The status is always a word in a badge, never a colour alone.

import type { StationClient, StationClientList } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, type BadgeTone, ProgressBar } from '@clavian/ui-web';
import type { ReactNode } from 'react';

export type ClientStatus =
  | 'unknown'
  | 'current'
  | 'available'
  | 'image'
  | 'checking'
  | 'downloading'
  | 'downloaded'
  | 'installing'
  | 'restarting'
  | 'rolled_back'
  | 'failed';

export interface ClientReading {
  readonly status: ClientStatus;
  readonly tone: BadgeTone;
  /** The version it is moving to, when it is moving (or could): shown as "Updating to …" / "… is available". */
  readonly toVersion: string | null;
  /** The finer phase the station reported (2026-10-07), or null from an older app. */
  readonly phase: NonNullable<StationClient['phase']> | null;
  /** The download's percent while it downloads and says how far; null otherwise. */
  readonly percent: number | null;
  /** How fast it downloads now, in bytes per second, while it downloads and says so (2026-10-07); null otherwise. */
  readonly bytesPerSecond: number | null;
}

const TONE: Readonly<Record<ClientStatus, BadgeTone>> = {
  unknown: 'neutral',
  current: 'ok',
  available: 'accent',
  image: 'accent',
  checking: 'info',
  downloading: 'info',
  downloaded: 'info',
  installing: 'info',
  restarting: 'info',
  rolled_back: 'warn',
  failed: 'warn',
};

/** The phases in which an update is moving: worth reading the list every few seconds. */
const MOVING_PHASES: ReadonlySet<string> = new Set(['checking', 'downloading', 'verifying', 'installing', 'restarting']);

/** Whether any station of the list is in the middle of an update (its download or install is moving). */
export function anyClientMoving(list: StationClientList | undefined): boolean {
  return (list?.data ?? []).some(
    (client) => (client.phase !== null && client.phase !== undefined && MOVING_PHASES.has(client.phase)) || client.state === 'downloading' || client.state === 'installing',
  );
}

/** What a station's client row says, as one status. `undefined` (no row) reads as not reported. */
export function readClient(client: StationClient | undefined): ClientReading {
  const status = clientStatus(client);
  const target = client?.targetVersion ?? null;
  const phase = client?.phase ?? null;
  const progress = phase === 'downloading' ? (client?.progress ?? null) : null;
  const percent = progress === null ? null : progress.percent;
  const bytesPerSecond = progress === null ? null : (progress.bytesPerSecond ?? null);
  return { status, tone: TONE[status], toVersion: target !== null && target !== client?.clientVersion ? target : null, phase, percent, bytesPerSecond };
}

type FormatNumber = (value: number, options?: Intl.NumberFormatOptions) => string;

/**
 * A download speed in words: "12 MB/s" from a megabyte a second up (one decimal under 10), else "850 kB/s". Decimal
 * units — a megabyte is 1,000,000 bytes and a kilobyte 1,000 — because that is what "MB" and "kB" mean.
 */
export function formatDownloadSpeed(bytesPerSecond: number, formatNumber: FormatNumber): string {
  const megabytes = bytesPerSecond / 1_000_000;
  // From 999.5 kB/s up it would round to "1,000 kB/s": say "1 MB/s" instead.
  if (bytesPerSecond >= 999_500) {
    return formatNumber(megabytes, { style: 'unit', unit: 'megabyte-per-second', unitDisplay: 'short', maximumFractionDigits: megabytes < 10 ? 1 : 0 });
  }
  return formatNumber(bytesPerSecond / 1_000, { style: 'unit', unit: 'kilobyte-per-second', unitDisplay: 'short', maximumFractionDigits: 0 });
}

function clientStatus(client: StationClient | undefined): ClientStatus {
  if (client === undefined || (client.clientVersion === null && client.state === null)) return 'unknown';
  // The finer phase first, when the station says it: checking and restarting have no state of their own.
  if (client.phase === 'checking') return 'checking';
  if (client.phase === 'restarting') return 'restarting';
  switch (client.state) {
    case 'rolled_back':
    case 'failed':
    case 'downloading':
    case 'downloaded':
    case 'installing':
      return client.state;
    default:
      break;
  }
  if (client.targetVersion !== null && client.targetVersion !== client.clientVersion) return isDisklessClient(client) ? 'image' : 'available';
  return client.clientVersion === null ? 'unknown' : 'current';
}

/** How a PC boots, as it last said (2026-10-07, spec/10 §14); null from an older app. */
export type ClientBoot = NonNullable<StationClient['boot']>;

/** Whether the PC is a diskless client (CCBoot on a normal boot): it does not install updates itself. */
export function isDisklessClient(client: StationClient | undefined): boolean {
  return client?.boot?.kind === 'diskless_client';
}

/** The diskless system's name for copy ("CCBoot"), or null when the PC did not recognise one by name. */
export function disklessSystem(boot: ClientBoot | null | undefined): string | null {
  return boot?.provider === 'ccboot' ? 'CCBoot' : null;
}

/** How the PC boots, in words: "Its own disk", "Diskless (CCBoot)", "Diskless (CCBoot) — updating the image". */
export function bootLabel(boot: ClientBoot, t: Translate): string {
  const system = disklessSystem(boot);
  switch (boot.kind) {
    case 'diskless_client':
      return system === null ? t('stations.client.boot.diskless') : t('stations.client.boot.disklessNamed', { system });
    case 'diskless_image_maintenance':
      return system === null ? t('stations.client.boot.maintenance') : t('stations.client.boot.maintenanceNamed', { system });
    default:
      return t('stations.client.boot.local');
  }
}

/** The status badge: its word, in its tone. */
export function ClientStatusBadge({ reading }: { readonly reading: ClientReading }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Badge tone={reading.tone} dot>
      {t(`stations.client.status.${reading.status}`)}
    </Badge>
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The line under a client, in plain words: from the station's phase when it reports one (2026-10-07) — "Downloading
 * update 1.10.8500 — 45%", "Installing — the PC restarts in a moment", "Up to date (1.10.8500)" — else, for an older
 * app, "Updating to 1.9.x" while it works on it and "1.9.x is available" before. A failure's words are its detail,
 * shown by the caller.
 */
export function clientMovement(reading: ClientReading, t: Translate, client?: StationClient, formatNumber: FormatNumber = String): string | null {
  const version = reading.toVersion ?? client?.targetVersion ?? null;
  switch (reading.phase) {
    case 'checking':
      return t('stations.client.phase.checking');
    case 'downloading':
      if (reading.percent === null) {
        return t('stations.client.phase.downloadingNoPercent', { version: version ?? t('stations.client.phase.theUpdate') });
      }
      return reading.bytesPerSecond === null
        ? t('stations.client.phase.downloading', { version: version ?? t('stations.client.phase.theUpdate'), percent: formatNumber(reading.percent) })
        : t('stations.client.phase.downloadingSpeed', {
            version: version ?? t('stations.client.phase.theUpdate'),
            percent: formatNumber(reading.percent),
            speed: formatDownloadSpeed(reading.bytesPerSecond, formatNumber),
          });
    case 'verifying':
      return t('stations.client.phase.verifying');
    case 'ready':
      return t('stations.client.phase.ready', { version: version ?? t('stations.client.phase.theUpdate') });
    case 'installing':
      return t('stations.client.phase.installing');
    case 'restarting':
      return t('stations.client.phase.restarting');
    case 'done':
      if (reading.status === 'current' && client?.clientVersion !== null && client?.clientVersion !== undefined) {
        return t('stations.client.phase.done', { version: client.clientVersion });
      }
      break;
    default:
      break;
  }
  if (reading.toVersion === null) return null;
  if (reading.status === 'downloading' || reading.status === 'downloaded' || reading.status === 'installing') {
    return t('stations.client.updatingTo', { version: reading.toVersion });
  }
  if (reading.status === 'available') return t('stations.client.availableVersion', { version: reading.toVersion });
  if (reading.status === 'image') {
    const system = disklessSystem(client?.boot);
    return system === null
      ? t('stations.client.image.movement', { version: reading.toVersion })
      : t('stations.client.image.movementNamed', { version: reading.toVersion, system });
  }
  return null;
}

/** The download's bar, while it downloads and the station says how far. */
export function ClientProgress({ reading, label, className, size = 'md' }: { readonly reading: ClientReading; readonly label: string; readonly className?: string; readonly size?: 'sm' | 'md' }): ReactNode {
  if (reading.percent === null) return null;
  return <ProgressBar value={reading.percent / 100} label={label} size={size} className={className} />;
}

/**
 * The Stations list's compact Client cell: the version it runs and a small status badge, with the movement
 * ("Downloading update 1.9.x — 45%" and its bar, "Updating to 1.9.x") or the failure's detail underneath. A station
 * with no report says so.
 */
export function ClientCell({ client }: { readonly client: StationClient | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const formatter = useVenueFormatter();
  const reading = readClient(client);
  const movement = clientMovement(reading, t, client, (value, options) => formatter.number(value, options));
  const detail = reading.status === 'failed' || reading.status === 'rolled_back' ? (client?.detail ?? null) : null;
  const version = client?.clientVersion ?? null;
  if (reading.status === 'unknown') return <span className="console-subtle">{t('stations.client.notReported')}</span>;
  return (
    <span className="console-station-client">
      <span className="console-station-client__line">
        {version === null ? null : <span className="console-num">{version}</span>}
        <ClientStatusBadge reading={reading} />
      </span>
      {movement === null ? null : <span className="console-subtle console-station-client__note">{movement}</span>}
      <ClientProgress reading={reading} label={movement ?? t('stations.client.title')} size="sm" className="console-station-client__bar" />
      {detail === null ? null : (
        <span className="console-subtle console-station-client__note" title={detail}>
          {t('stations.client.phase.failed', { detail })}
        </span>
      )}
    </span>
  );
}
